import React, { useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle } from './ui/Card';
import { ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  format?: (value: any, row: any) => React.ReactNode;
}
interface ComparisonTableProps {
  title: string;
  data: any[];
  columns: Column[];
  onRowClick?: (row: any) => void;
}
export function ComparisonTable({
  title,
  data,
  columns,
  onRowClick
}: ComparisonTableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aValue = keyPath(a, sortKey);
      const bValue = keyPath(b, sortKey);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDirection]);
  // Helper to access nested properties like 'effort.totalCalls'
  function keyPath(obj: any, path: string) {
    return path.split('.').reduce((o, i) => o?.[i], obj);
  }
  return <Card className="overflow-hidden" noPadding data-id="element-1">
      <div className="p-6 border-b border-slate-200" data-id="element-2">
        <CardTitle data-id="element-3">{title}</CardTitle>
      </div>
      <div className="overflow-x-auto" data-id="element-4">
        <table className="w-full text-sm text-left" data-id="element-5">
          <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-6">
            <tr data-id="element-7">
              {columns.map(col => <th key={col.key} className={clsx('px-6 py-4 whitespace-nowrap transition-colors', col.sortable && 'cursor-pointer hover:bg-slate-100', col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')} onClick={() => col.sortable && handleSort(col.key)} data-id="element-8">
                  <div className={clsx('flex items-center gap-1', col.align === 'right' && 'justify-end', col.align === 'center' && 'justify-center')} data-id="element-9">
                    {col.label}
                    {col.sortable && <ArrowUpDown className={clsx('w-3 h-3', sortKey === col.key ? 'text-indigo-600' : 'text-slate-300')} data-id="element-10" />}
                  </div>
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white" data-id="element-11">
            {sortedData.map((row, idx) => <tr key={idx} className={clsx('hover:bg-slate-50 transition-colors', onRowClick && 'cursor-pointer')} onClick={() => onRowClick && onRowClick(row)} data-id="element-12">
                {columns.map(col => {
              const val = keyPath(row, col.key);
              return <td key={col.key} className={clsx('px-6 py-4 whitespace-nowrap text-slate-700', col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')} data-id="element-13">
                      {col.format ? col.format(val, row) : val}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
    </Card>;
}